import { DEFAULT_TICKET_MESSAGE } from './ticketMessage'

export type EventStatus = 'draft' | 'published'
export type TicketingMode = 'not_ticketed' | 'ticketed'
/** Single: each attendee checks in once. Multiple: intentional repeat check-ins, e.g. each day. */
export type CheckInMode = 'single' | 'multiple'

/** Four optional planning metrics; each is set or unset on its own. */
export type EventRecord = Readonly<{
  id: string
  name: string
  eventDate: string
  endDate: string | null
  startTime: string | null
  endTime: string | null
  timezone: string
  location: string
  status: EventStatus
  ticketingMode: TicketingMode
  checkInMode: CheckInMode
  /** Optional ClickSend Sender ID. Empty uses the environment account default. */
  smsSender: string | null
  /** The text each ticket is sent with; placeholders such as {{firstName}} are filled per person. */
  ticketMessageTemplate: string
  /** Whole number of participants the event aims to reach. */
  attendanceGoal: number | null
  /** Share of registrations expected to check in, as a percentage (0-100). */
  turnoverRatePercentage: number | null
  /** Planned outreach hours per day. */
  outreachHoursPerDay: number | null
  /** Planned days of outreach. */
  outreachDays: number | null
  createdBy: string
  createdAt: string
  updatedAt: string
}>

export type EventInput = Omit<
  EventRecord,
  'id' | 'createdBy' | 'createdAt' | 'updatedAt'
>

export type EventRow = Readonly<{
  id: string
  name: string
  event_date: string
  end_date: string | null
  start_time: string | null
  end_time: string | null
  timezone: string
  location: string
  status: EventStatus
  ticketing_mode: TicketingMode
  check_in_mode: CheckInMode
  sms_sender?: string | null
  ticket_message_template?: string | null
  attendance_goal: number | null
  turnover_rate_percentage: number | null
  outreach_hours_per_day: number | null
  outreach_days: number | null
  created_by: string
  created_at: string
  updated_at: string
}>

export function getSupportedTimezones(): string[] {
  const supportedValuesOf = Intl.supportedValuesOf as
    | ((key: 'timeZone') => string[])
    | undefined
  try {
    return supportedValuesOf?.('timeZone') ?? []
  } catch {
    return []
  }
}

export function getDefaultTimezone(): string {
  const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone
  const supported = getSupportedTimezones()
  if (resolved && (supported.length === 0 || supported.includes(resolved))) return resolved
  return 'Europe/London'
}

export function createEventDefaults(): EventInput {
  return {
    name: '',
    eventDate: '',
    endDate: null,
    startTime: null,
    endTime: null,
    timezone: getDefaultTimezone(),
    location: '',
    status: 'published',
    ticketingMode: 'not_ticketed',
    checkInMode: 'single',
    smsSender: null,
    ticketMessageTemplate: DEFAULT_TICKET_MESSAGE,
    attendanceGoal: null,
    turnoverRatePercentage: null,
    outreachHoursPerDay: null,
    outreachDays: null,
  }
}

export function mapEventRow(row: EventRow): EventRecord {
  return {
    id: row.id,
    name: row.name,
    eventDate: row.event_date,
    endDate: row.end_date,
    startTime: row.start_time,
    endTime: row.end_time,
    timezone: row.timezone,
    location: row.location,
    status: row.status,
    ticketingMode: row.ticketing_mode,
    // Rows cached before check-in mode existed read as the default.
    checkInMode: row.check_in_mode ?? 'single',
    smsSender: row.sms_sender?.trim() || null,
    // Rows cached before templates existed read as the default.
    ticketMessageTemplate: row.ticket_message_template ?? DEFAULT_TICKET_MESSAGE,
    attendanceGoal: row.attendance_goal === null ? null : Number(row.attendance_goal),
    turnoverRatePercentage: row.turnover_rate_percentage === null ? null : Number(row.turnover_rate_percentage),
    outreachHoursPerDay: row.outreach_hours_per_day === null ? null : Number(row.outreach_hours_per_day),
    outreachDays: row.outreach_days === null ? null : Number(row.outreach_days),
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function toEventRowInput(input: EventInput) {
  return {
    name: input.name.trim(),
    event_date: input.eventDate,
    end_date: input.endDate || null,
    start_time: input.startTime || null,
    end_time: input.endTime || null,
    timezone: input.timezone,
    location: input.location.trim(),
    status: input.status,
    ticketing_mode: input.ticketingMode,
    check_in_mode: input.checkInMode,
    sms_sender: input.smsSender?.trim() || null,
    ticket_message_template: input.ticketMessageTemplate,
    attendance_goal: input.attendanceGoal ?? null,
    turnover_rate_percentage: input.turnoverRatePercentage ?? null,
    outreach_hours_per_day: input.outreachHoursPerDay ?? null,
    outreach_days: input.outreachDays ?? null,
  }
}
